import { useEffect, useState, useCallback } from "react";
import { Zap, Plus, Play, Pause, Archive, FlaskConical, RefreshCw, X, AlertTriangle, CheckCircle2, Clock, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";

const TRIGGERS = [
  "booking.completed", "booking.created", "booking.cancelled", "booking.no_show",
  "grooming.completed", "invoice.created", "invoice.paid", "invoice.overdue",
  "payment.received", "customer.created", "pet.created", "vaccination.due_soon",
];
const OPERATORS = ["equals", "not_equals", "greater_than", "less_than", "contains", "exists", "not_exists", "days_since", "days_until", "date_before", "date_after"];
const VALUELESS = ["exists", "not_exists"];
const FIELD_ROOTS = ["event", "customer", "pet", "booking", "invoice", "payment", "refund"];
const ACTIONS = ["CREATE_TASK", "CREATE_FOLLOWUP", "SEND_NOTIFICATION", "ADD_TAG", "REMOVE_TAG", "GENERATE_INSIGHT"];
const ROLES = ["owner", "admin", "manager", "receptionist", "vet", "groomer", "trainer", "accountant"];

const TABS = [
  { k: "active", label: "Active" },
  { k: "draft", label: "Drafts" },
  { k: "suggested", label: "Suggested" },
  { k: "paused", label: "Paused" },
  { k: "archived", label: "Archived" },
  { k: "history", label: "History" },
];

const STATUS_STYLE = {
  active: { bg: "#E7F7E4", fg: "#1F7A33" }, draft: { bg: "#EEE8DD", fg: "#7B6D62" },
  paused: { bg: "#FDF2D6", fg: "#8A6D1A" }, archived: { bg: "#F1ECE4", fg: "#9A8E82" },
};

function StatusPill({ s }) {
  const st = STATUS_STYLE[s] || STATUS_STYLE.draft;
  return <span data-testid={`status-${s}`} className="rounded-full px-2.5 py-0.5 text-xs font-bold" style={{ background: st.bg, color: st.fg }}>{s}</span>;
}

function Arrow() { return <div className="my-1 ml-3 h-4 w-px bg-[#EEE8DD]" />; }

export default function AutomationCenter() {
  const { user } = useAuth();
  const canEdit = hasPerm(user, "settings.edit");
  const engineOn = !!(user?.tenant?.feature_flags?.automation_engine);
  const shallyOn = !!(user?.tenant?.feature_flags?.shally_workflow_builder);
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(null);
  const [execs, setExecs] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showShally, setShowShally] = useState(false);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const statusParam = ["suggested", "history"].includes(tab) ? "" : tab;
      const { data } = await api.get("/automations", { params: { status: statusParam, limit: 200 } });
      let rows = data.items || [];
      if (tab === "suggested") rows = rows.filter((r) => r.status === "draft" && (r.source === "shally" || r.source_workflow_id));
      setItems(rows);
    } catch (e) { toast.error(apiErr(e)); setItems([]); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (a) => {
    setSel(a); setExecs(null);
    try { const { data } = await api.get(`/automations/${a.id}/executions`, { params: { limit: 50 } }); setExecs(data.items || []); }
    catch { setExecs([]); }
  };

  const act = async (a, verb) => {
    try {
      const { data } = await api.post(`/automations/${a.id}/${verb}`);
      toast.success(`Automation ${data.status || verb}`);
      if ((data.warnings || []).length) toast.warning(data.warnings[0]);
      await load();
      if (sel?.id === a.id) { const { data: fresh } = await api.get(`/automations/${a.id}`); setSel(fresh); }
    } catch (e) { toast.error(apiErr(e)); }
  };

  const dryRun = async (a) => {
    try { const { data } = await api.post(`/automations/${a.id}/dry-run`); toast.success(`Dry run: ${data.matched ?? data.count ?? 0} record(s) would match`); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const runProcess = async () => {
    try { const { data } = await api.post(`/automations/process`); toast.success(`Processed ${data.processed ?? 0} due execution(s)`); if (sel) openDetail(sel); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="automation-center" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-extrabold text-foreground"><Zap className="h-6 w-6 text-primary" /> Automation Center</h1>
          <p className="text-sm text-muted-foreground">Set it once — Pawkt runs the repetitive work for you.</p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && <button data-testid="build-with-shally-btn" onClick={() => setShowShally(true)} className="flex items-center gap-1.5 rounded-xl border border-[#D7C9FB] bg-[#EEE9FE] px-3 py-2 text-sm font-bold text-[#5837D8] hover:bg-[#E5DCFB]"><Sparkles className="h-4 w-4" /> Build with Shally</button>}
          <button data-testid="run-process-btn" onClick={runProcess} className="flex items-center gap-1.5 rounded-xl border border-[#EEE8DD] bg-white px-3 py-2 text-sm font-semibold text-foreground hover:bg-[#FAF8F5]" disabled={!canEdit}><RefreshCw className="h-4 w-4" /> Run due now</button>
          <button data-testid="create-automation-btn" onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-sm font-bold text-white hover:bg-[#6A48F0]" disabled={!canEdit}><Plus className="h-4 w-4" /> New automation</button>
        </div>
      </div>

      {!engineOn && (
        <div data-testid="engine-disabled-banner" className="flex items-start gap-3 rounded-2xl border border-[#F3E09A] bg-[#FDF8E7] p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#8A6D1A]" />
          <div><p className="text-sm font-bold text-[#5B4B14]">Automation execution is currently disabled for this workspace.</p>
            <p className="text-sm text-[#8A6D1A]">You can create and configure automations now. They will start running automatically once the Automation Engine is enabled for your account — nothing runs, and no results are fabricated, while it's off.</p></div>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 border-b border-[#EEE8DD]">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => { setTab(t.k); setSel(null); }}
            className="rounded-t-lg px-3.5 py-2 text-sm font-semibold transition-colors"
            style={tab === t.k ? { color: "#5837D8", borderBottom: "2px solid #7C5CFC" } : { color: "#7B6D62" }}>{t.label}</button>
        ))}
      </div>

      {items === null ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div data-testid="empty-state" className="rounded-2xl border border-dashed border-[#EEE8DD] bg-[#FAF8F5] p-12 text-center">
          <Zap className="mx-auto h-8 w-8 text-primary/60" />
          <p className="mt-3 text-base font-bold text-foreground">
            {tab === "suggested" ? "No Shally-suggested automations yet" : tab === "history" ? "No automations yet" : `No ${tab} automations`}
          </p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            {tab === "suggested" ? "When you build a workflow with Shally, its proposals appear here for your approval." : "Create an automation to turn a repeating event — like a completed grooming — into a follow-up task your team never forgets."}
          </p>
          {canEdit && tab !== "suggested" && <button onClick={() => setShowCreate(true)} className="mt-4 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white">Create your first automation</button>}
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((a) => (
            <button key={a.id} data-testid={`automation-row-${a.id}`} onClick={() => openDetail(a)}
              className="flex items-center justify-between gap-4 rounded-2xl border border-[#EEE8DD] bg-white p-4 text-left transition-all hover:shadow-md">
              <div className="min-w-0">
                <div className="flex items-center gap-2"><p className="truncate font-bold text-foreground">{a.name}</p><StatusPill s={a.status} /></div>
                <p className="mt-0.5 truncate text-sm text-muted-foreground">
                  <span className="font-semibold">When</span> {a.trigger?.event_type || "—"}
                  {(a.conditions || []).length ? ` · ${a.conditions.length} condition(s)` : ""}
                  {a.wait?.days ? ` · wait ${a.wait.days}d` : ""}
                  {` · ${(a.actions || []).length} action(s)`}
                </p>
              </div>
              <div className="shrink-0 text-right text-xs text-muted-foreground">
                <p>v{a.version || 1}</p>
                <p>{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : ""}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {sel && <DetailDrawer a={sel} execs={execs} canEdit={canEdit} onClose={() => setSel(null)} onAct={act} onDryRun={dryRun} />}
      {showCreate && <CreateDialog onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); setTab("draft"); load(); }} />}
      {showShally && <ShallyBuilderDialog shallyOn={shallyOn} onClose={() => setShowShally(false)} onActivated={() => { setShowShally(false); setTab("active"); load(); }} onSuggested={() => { setShowShally(false); setTab("suggested"); load(); }} />}
    </div>
  );
}

function Step({ tag, color, children }) {
  return (
    <div className="flex flex-col items-start">
      <div className="w-full rounded-xl border border-[#EEE8DD] bg-[#FAF8F5] px-4 py-3" style={{ borderLeft: `4px solid ${color}` }}>
        <p className="text-[10px] font-extrabold uppercase tracking-wider" style={{ color }}>{tag}</p>
        <div className="mt-0.5 text-sm font-semibold text-foreground">{children}</div>
      </div>
    </div>
  );
}

function DetailDrawer({ a, execs, canEdit, onClose, onAct, onDryRun }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div data-testid="automation-detail" className="h-full w-full max-w-lg overflow-y-auto bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div><div className="flex items-center gap-2"><h2 className="text-xl font-extrabold text-foreground">{a.name}</h2><StatusPill s={a.status} /></div>
            {a.description && <p className="mt-1 text-sm text-muted-foreground">{a.description}</p>}</div>
          <button data-testid="close-detail" onClick={onClose} className="rounded-lg p-1.5 hover:bg-[#FAF8F5]"><X className="h-5 w-5" /></button>
        </div>

        <div className="mt-6 space-y-0">
          <Step tag="When" color="#1F7A33">{a.trigger?.event_type || "—"}</Step>
          <Arrow />
          <Step tag="If" color="#8A6D1A">
            {(a.conditions || []).length === 0 ? <span className="text-muted-foreground">Any — no conditions</span> :
              (a.conditions).map((c, i) => <div key={i}>{c.field} <b>{c.op}</b> {VALUELESS.includes(c.op) ? "" : JSON.stringify(c.value)}</div>)}
          </Step>
          <Arrow />
          <Step tag="Wait" color="#5837D8">{a.wait?.days ? `${a.wait.days} day(s)` : "No delay"}</Step>
          <Arrow />
          <Step tag="Then" color="#7C5CFC">
            {(a.actions || []).map((ac, i) => <div key={i} className="mt-0.5">{ac.type}{ac.payload?.title ? ` — ${ac.payload.title}` : ac.payload?.reason ? ` — ${ac.payload.reason}` : ac.payload?.tag ? ` — ${ac.payload.tag}` : ""}</div>)}
          </Step>
        </div>

        {canEdit && (
          <div className="mt-6 flex flex-wrap gap-2">
            {a.status !== "active" && a.status !== "archived" && <button data-testid="activate-btn" onClick={() => onAct(a, "activate")} className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-bold text-white"><Play className="h-4 w-4" /> Activate</button>}
            {a.status === "active" && <button data-testid="pause-btn" onClick={() => onAct(a, "pause")} className="flex items-center gap-1.5 rounded-xl border border-[#EEE8DD] px-3 py-2 text-sm font-bold text-foreground"><Pause className="h-4 w-4" /> Pause</button>}
            {a.status === "paused" && <button data-testid="resume-btn" onClick={() => onAct(a, "activate")} className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-bold text-white"><Play className="h-4 w-4" /> Resume</button>}
            <button data-testid="dryrun-btn" onClick={() => onDryRun(a)} className="flex items-center gap-1.5 rounded-xl border border-[#EEE8DD] px-3 py-2 text-sm font-bold text-foreground"><FlaskConical className="h-4 w-4" /> Dry run</button>
            {a.status !== "archived" && <button data-testid="archive-btn" onClick={() => { if (window.confirm("Archive this automation? It will stop running and cannot be edited.")) onAct(a, "archive"); }} className="flex items-center gap-1.5 rounded-xl border border-[#EEE8DD] px-3 py-2 text-sm font-bold text-[#B4443A]"><Archive className="h-4 w-4" /> Archive</button>}
          </div>
        )}

        <div className="mt-8">
          <h3 className="text-sm font-extrabold uppercase tracking-wide text-muted-foreground">Execution history</h3>
          {execs === null ? <p className="mt-3 text-sm text-muted-foreground">Loading…</p> :
            execs.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No executions yet.</p> : (
              <div className="mt-3 space-y-2">
                {execs.map((e) => (
                  <div key={e.id} data-testid={`exec-${e.id}`} className="rounded-xl border border-[#EEE8DD] p-3">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-sm font-bold text-foreground">
                        {e.status === "completed" ? <CheckCircle2 className="h-4 w-4 text-[#1F7A33]" /> : e.status === "failed" ? <AlertTriangle className="h-4 w-4 text-[#B4443A]" /> : <Clock className="h-4 w-4 text-[#8A6D1A]" />}
                        {e.status}{e.retry_count ? ` · retry ${e.retry_count}` : ""}
                      </span>
                      <span className="text-xs text-muted-foreground">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
                    </div>
                    {e.current_step && <p className="mt-1 text-xs text-muted-foreground">Step: {e.current_step}</p>}
                    {e.error && <p className="mt-1 text-xs text-[#B4443A]">{String(e.error).slice(0, 160)}</p>}
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function CreateDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState(TRIGGERS[0]);
  const [useCond, setUseCond] = useState(false);
  const [cField, setCField] = useState("booking.service_type");
  const [cOp, setCOp] = useState("equals");
  const [cVal, setCVal] = useState("");
  const [waitDays, setWaitDays] = useState("");
  const [actionType, setActionType] = useState("CREATE_TASK");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [detail, setDetail] = useState("");
  const [reason, setReason] = useState("");
  const [tag, setTag] = useState("");
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);

  const needsTitle = ["CREATE_TASK", "SEND_NOTIFICATION", "GENERATE_INSIGHT"].includes(actionType);

  const submit = async () => {
    const payload = {};
    if (needsTitle) payload.title = title;
    if (actionType === "SEND_NOTIFICATION") payload.message = message;
    if (actionType === "GENERATE_INSIGHT") payload.detail = detail;
    if (actionType === "CREATE_FOLLOWUP") payload.reason = reason;
    if (["ADD_TAG", "REMOVE_TAG"].includes(actionType)) payload.tag = tag;
    if (actionType === "CREATE_TASK" && role) payload.assigned_role = role;
    const body = {
      name, trigger: { event_type: trigger }, actions: [{ type: actionType, payload }],
      conditions: useCond ? [{ field: cField, op: cOp, ...(VALUELESS.includes(cOp) ? {} : { value: cVal }) }] : [],
      wait: waitDays ? { days: Number(waitDays) } : null,
    };
    setBusy(true);
    try {
      const { data } = await api.post("/automations", body);
      if ((data.validation_errors || []).length) toast.warning(`Saved as draft with warnings: ${data.validation_errors[0]}`);
      else toast.success("Automation created as draft");
      if ((data.warnings || []).length) toast.warning(data.warnings[0]);
      onCreated();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const inp = "w-full rounded-xl border border-[#EEE8DD] bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40";
  const lbl = "text-xs font-extrabold uppercase tracking-wide text-muted-foreground";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="create-dialog" className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h2 className="text-lg font-extrabold text-foreground">New automation</h2><button onClick={onClose} className="rounded-lg p-1.5 hover:bg-[#FAF8F5]"><X className="h-5 w-5" /></button></div>
        <div className="mt-4 space-y-4">
          <div><label className={lbl}>Name</label><input data-testid="field-name" className={inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Grooming follow-up" /></div>
          <div><label className={lbl}>When (trigger)</label><select data-testid="field-trigger" className={inp} value={trigger} onChange={(e) => setTrigger(e.target.value)}>{TRIGGERS.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
          <div className="rounded-xl border border-[#EEE8DD] p-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-foreground"><input type="checkbox" checked={useCond} onChange={(e) => setUseCond(e.target.checked)} /> Add a condition (If)</label>
            {useCond && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                <input className={inp} value={cField} onChange={(e) => setCField(e.target.value)} placeholder="field (e.g. booking.x)" />
                <select className={inp} value={cOp} onChange={(e) => setCOp(e.target.value)}>{OPERATORS.map((o) => <option key={o}>{o}</option>)}</select>
                {!VALUELESS.includes(cOp) && <input className={inp} value={cVal} onChange={(e) => setCVal(e.target.value)} placeholder="value" />}
                <p className="col-span-3 text-[11px] text-muted-foreground">Field root must be one of: {FIELD_ROOTS.join(", ")}</p>
              </div>
            )}
          </div>
          <div><label className={lbl}>Wait (days, optional)</label><input data-testid="field-wait" type="number" min="0" max="3650" className={inp} value={waitDays} onChange={(e) => setWaitDays(e.target.value)} placeholder="e.g. 30" /></div>
          <div><label className={lbl}>Then (action)</label><select data-testid="field-action" className={inp} value={actionType} onChange={(e) => setActionType(e.target.value)}>{ACTIONS.map((a) => <option key={a}>{a}</option>)}</select></div>
          {needsTitle && <div><label className={lbl}>Title</label><input data-testid="field-title" className={inp} value={title} onChange={(e) => setTitle(e.target.value)} /></div>}
          {actionType === "SEND_NOTIFICATION" && <div><label className={lbl}>Message</label><textarea className={inp} value={message} onChange={(e) => setMessage(e.target.value)} /></div>}
          {actionType === "GENERATE_INSIGHT" && <div><label className={lbl}>Detail</label><textarea className={inp} value={detail} onChange={(e) => setDetail(e.target.value)} /></div>}
          {actionType === "CREATE_FOLLOWUP" && <div><label className={lbl}>Reason</label><input className={inp} value={reason} onChange={(e) => setReason(e.target.value)} /></div>}
          {["ADD_TAG", "REMOVE_TAG"].includes(actionType) && <div><label className={lbl}>Tag</label><input className={inp} value={tag} onChange={(e) => setTag(e.target.value)} /></div>}
          {actionType === "CREATE_TASK" && <div><label className={lbl}>Assign role (optional)</label><select className={inp} value={role} onChange={(e) => setRole(e.target.value)}><option value="">—</option>{ROLES.map((r) => <option key={r}>{r}</option>)}</select></div>}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border border-[#EEE8DD] px-4 py-2 text-sm font-bold text-foreground">Cancel</button>
          <button data-testid="submit-create" disabled={busy || !name} onClick={submit} className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50">{busy ? "Saving…" : "Save as draft"}</button>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">Automations are saved as drafts. Review and Activate when ready. External sends are not part of this builder.</p>
      </div>
    </div>
  );
}

function ShallyBuilderDialog({ shallyOn, onClose, onActivated, onSuggested }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [wf, setWf] = useState(null); // generate response {id,status,definition,validation,preview}
  const inp = "w-full rounded-xl border border-[#EEE8DD] bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40";

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shally/workflows/generate", { message });
      setWf(data);
      if (!data.validation?.valid) toast.warning("Shally drafted it, but it needs a tweak before approval.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/shally/workflows/${wf.id}/approve`);
      setWf((w) => ({ ...w, status: data.status }));
      toast.success("Approved. You can activate it now.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/shally/workflows/${wf.id}/activate`);
      if (data.executable) { toast.success("Workflow is now active."); onActivated(); }
      else { toast.success("Workflow activated. It will run once the Automation Engine is enabled."); onActivated(); }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const status = wf?.status;
  const valid = wf?.validation?.valid;
  const preview = wf?.preview;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="shally-builder-dialog" className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-foreground"><Sparkles className="h-5 w-5 text-primary" /> Build with Shally</h2>
          <button data-testid="shally-close" onClick={onClose} className="rounded-lg p-1.5 hover:bg-[#FAF8F5]"><X className="h-5 w-5" /></button>
        </div>

        {!shallyOn ? (
          <div data-testid="shally-disabled" className="mt-4 rounded-xl border border-[#F3E09A] bg-[#FDF8E7] p-4 text-sm text-[#8A6D1A]">
            The Shally Workflow Builder is not enabled for this workspace yet. Once enabled, you can describe an automation in plain words and Shally will draft it for your approval — nothing is created or run without you.
          </div>
        ) : (
          <>
            <p className="mt-2 text-sm text-muted-foreground">Describe what you want to automate in plain words. Shally proposes it, Pawkt validates it, and nothing runs until you approve and activate.</p>
            {/* Step flow indicator */}
            <div className="mt-4 flex flex-wrap items-center gap-1.5 text-[11px] font-bold">
              {["Generate", "Validate", "Preview", "Approve", "Activate"].map((s, i) => (
                <span key={s} className="flex items-center gap-1.5">
                  <span className="rounded-full px-2.5 py-1" style={{
                    background: (i <= 1 && wf) || (i === 2 && valid) || (i === 3 && status === "APPROVED") ? "#EEE9FE" : "#F1ECE4",
                    color: (i <= 1 && wf) || (i === 2 && valid) || (i === 3 && status === "APPROVED") ? "#5837D8" : "#9A8E82",
                  }}>{s}</span>
                  {i < 4 && <ArrowRight className="h-3 w-3 text-[#CDBEEF]" />}
                </span>
              ))}
            </div>

            <div className="mt-4">
              <textarea data-testid="shally-message" className={inp} rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="e.g. When a grooming is completed, wait 30 days and create a follow-up to rebook." />
            </div>
            <button data-testid="shally-generate" disabled={busy || !message.trim()} onClick={generate}
              className="mt-2 flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
              <Sparkles className="h-4 w-4" /> {busy && !wf ? "Shally is thinking…" : wf ? "Regenerate" : "Generate with Shally"}
            </button>

            {wf && (
              <div data-testid="shally-result" className="mt-5 rounded-xl border border-[#EEE8DD] bg-[#FAF8F5] p-4">
                <p className="font-bold text-foreground">{preview?.name || wf.definition?.name || "Proposed workflow"}</p>
                {preview?.description && <p className="mt-0.5 text-sm text-muted-foreground">{preview.description}</p>}
                {valid ? (
                  <div className="mt-3 space-y-2 text-sm">
                    <p><span className="font-bold text-foreground">When:</span> {preview?.when?.event_type || "—"}</p>
                    <div><span className="font-bold text-foreground">Then:</span>
                      <ul className="mt-1 list-disc pl-5 text-muted-foreground">{(preview?.then || []).map((t, i) => <li key={i}>{t}</li>)}</ul>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Requires your confirmation. Consequential actions never run automatically.</p>
                  </div>
                ) : (
                  <div data-testid="shally-errors" className="mt-3 rounded-lg bg-[#FDEBE8] p-3 text-sm text-[#B4443A]">
                    <p className="font-bold">Needs a tweak:</p>
                    <ul className="mt-1 list-disc pl-5">{(wf.validation?.errors || []).map((e, i) => <li key={i}>{e}</li>)}</ul>
                  </div>
                )}
                <span className="mt-3 inline-block rounded-full px-2.5 py-0.5 text-xs font-bold" style={{ background: "#EEE9FE", color: "#5837D8" }}>Status: {status}</span>

                <div className="mt-4 flex flex-wrap gap-2">
                  {valid && status !== "APPROVED" && status !== "ACTIVE" &&
                    <button data-testid="shally-approve" disabled={busy} onClick={approve} className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-bold text-white disabled:opacity-50"><CheckCircle2 className="h-4 w-4" /> Approve</button>}
                  {status === "APPROVED" &&
                    <button data-testid="shally-activate" disabled={busy} onClick={activate} className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-bold text-white disabled:opacity-50"><Play className="h-4 w-4" /> Activate</button>}
                  <button data-testid="shally-view-suggested" onClick={onSuggested} className="rounded-xl border border-[#EEE8DD] px-3 py-2 text-sm font-bold text-foreground">View in Suggested</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
